import { useApolloClient } from '@apollo/client';
import { Stack, Typography } from '@mui/material';
import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Accordion } from '@/components/Accordion/Accordion';
import { TypographyClamped } from '@/components/typography/TypographyClamped';
import { useIsElementHovered } from '@/hooks/useIsElementHovered';
import { useModalState } from '@/hooks/useModalState';
import { DispositiveProvisionSuggestionsProvider } from '@/modules/aiDP/context/DispositiveProvisionSuggestions.provider';
import { AIOnboardingModal } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal';
import {
  AIOnboardingModalVariant,
  OpenAIOnboardingModalProps,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { AISuggestionReviewCard } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard';
import { AIOnboardingSuggestionDrawer } from '@/modules/aiSuggestions/components/AIOnboardingSuggestionDrawer';
import { LocateSuggestionButton } from '@/modules/aiSuggestions/components/LocateSuggestionButton';
import { EntitySuggestionsProvider } from '@/modules/aiSuggestions/context/EntitySuggestions.provider';
import { useAssetProviderDataLinkingState } from '@/modules/assetProviderIntegrations/shared/hooks/useEnabledAssetsIntegrations';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { useCanManageIntegrationData } from '@/modules/authentication/hooks/useCanManageIntegrationData';
import { NoDispositionsBadge } from '@/modules/dispositiveProvisions/NoDispositionsBadge';
import { EntityActionsMenu } from '@/modules/entities/components/EntityActionsMenu/EntityActionsMenu';
import { EntityDetailsAwareRoute } from '@/modules/entities/contexts/entityDetails/EntityDetailsAwareRoute';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EditEntitySplitScreenModal } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreenModal';
import { EntityType } from '@/modules/entities/types/EntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { AddHoldingsFormModal } from '@/modules/estate/components/AddHoldingsFormModal/AddHoldingsFormModal';
import { DraftGRATActions } from '@/modules/estate/components/DraftGRATActions/DraftGRATActions';
import { useNavigateToGRATPageByStage } from '@/modules/estate/components/DraftGRATActions/useNavigateToGRATPageByStage';
import { EntityAccordionContent } from '@/modules/estate/components/EntityAccordionContent/EntityAccordionContent';
import { HoldingsGroupActions } from '@/modules/estate/components/HoldingsGroupActions/HoldingsGroupActions';
import { IntegrationConnectionWarningModal } from '@/modules/estate/components/IntegrationConnectionWarningModal';
import { useEstateHoldingsContext } from '@/modules/estate/contexts/estateHoldings.context';
import { useEstateOnboardingUnacknowledgedSuggestionsContext } from '@/modules/estate/estateAIOnboarding/context/estateOnboardingUnacknowledgedSuggestions.context';
import {
  getUnacknowledgedHoldingsSuggestionsWhere,
  isEstateHoldingSuggestion,
} from '@/modules/estate/estateAIOnboarding/estateOnboarding.utils';
import { sumHoldingsGroupCurrentValue } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/DirectHoldingsGroupContainer.utils';
import { LinkIntegrationEntityFormModal } from '@/modules/estate/EstateDirectHoldings/DirectHoldingsGroupContainer/LinkIntegrationEntityFormModal/LinkIntegrationEntityFormModal';
import {
  EstatePage_EntityFragment,
  EstatePageDataDocument,
} from '@/modules/estate/graphql/EstatePageData.generated';
import { useEstateSuggestionGenerationState } from '@/modules/estate/hooks/useEstateSuggestionGenerationState';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { WorkflowTrackerKind } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getIdSafeString } from '@/utils/formatting/strings';

import { useFormattedTrustSubtitle } from './EstateTrustEntityCard.utils';

export type TrustCardVariant = 'draft_grats' | 'default';

export interface EstateTrustEntityCardProps {
  trusts: EstatePage_EntityFragment[];
  variant?: TrustCardVariant;
}

export function EstateTrustEntityCard({
  trusts,
  variant = 'default',
}: EstateTrustEntityCardProps) {
  // Sort trusts by descending order of net value (highest to lowest)
  const sortedTrusts = useMemo(() => {
    return [...trusts].sort((a, b) => {
      const valueA = sumHoldingsGroupCurrentValue([a]);
      const valueB = sumHoldingsGroupCurrentValue([b]);
      return valueB.cmp(valueA); // Descending order (highest first)
    });
  }, [trusts]);

  if (!trusts.length) {
    return null;
  }

  return (
    <Stack spacing={1}>
      {sortedTrusts.map((trust) => (
        <TrustSection
          key={`trust-${trust.id}`}
          trust={trust}
          variant={variant}
        />
      ))}
    </Stack>
  );
}

export interface TrustSectionProps {
  trust: EstatePage_EntityFragment;
  variant?: TrustCardVariant;
  onEditEntitySection?: (entityId: string, section: EditEntitySection) => void;
}

export function TrustSection({
  trust,
  variant = 'default',
  onEditEntitySection,
}: TrustSectionProps) {
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  const canEdit = useCanEditHouseholdData();
  const canManageIntegrationData = useCanManageIntegrationData();
  const { householdId } = useHouseholdDetailsContext();
  const refetchEstatePageData = useCallback(() => {
    void apolloClient.refetchQueries({ include: [EstatePageDataDocument] });
  }, [apolloClient]);
  const [isAddHoldingModalOpen, setIsAddHoldingModalOpen] = useState(false);
  const [isLinkIntegrationModalOpen, setIsLinkIntegrationModalOpen] =
    useState(false);
  const [
    isIntegrationConnectionWarningModalOpen,
    setIsIntegrationConnectionWarningModalOpen,
  ] = useState(false);

  const { integrationsAvailableForDataLinking, canConnectToIntegrations } =
    useAssetProviderDataLinkingState();

  const handleLinkIntegrationClick = useCallback(() => {
    if (!householdId) return;

    if (integrationsAvailableForDataLinking.length === 0) {
      setIsIntegrationConnectionWarningModalOpen(true);
      return;
    }

    setIsLinkIntegrationModalOpen(true);
  }, [householdId, integrationsAvailableForDataLinking.length]);

  const [isHovered, hoverProps] = useIsElementHovered();
  const { additionalReferenceSuggestions, refetch: refetchSuggestions } =
    useEstateOnboardingUnacknowledgedSuggestionsContext();
  const { unacknowledgedSuggestionsV2 } = useEstateHoldingsContext();
  const isGeneratingEntityDetailsSuggestions =
    useEstateSuggestionGenerationState({
      entityId: trust.id,
      workflowTrackerKind:
        WorkflowTrackerKind.AiSuggestionsV2EntityDetailsGeneration,
      onCompleted: refetchSuggestions,
    });
  const isGeneratingDPSuggestions = useEstateSuggestionGenerationState({
    entityId: trust.id,
    workflowTrackerKind: WorkflowTrackerKind.AiSuggestionsV2DpGeneration,
    onCompleted: refetchSuggestions,
  });
  const isGeneratingSuggestions =
    isGeneratingEntityDetailsSuggestions || isGeneratingDPSuggestions;
  // this is used for draft GRATs
  const navigateToGRATPageByStage = useNavigateToGRATPageByStage();

  const entitySuggestionsV2 = unacknowledgedSuggestionsV2[trust.id];
  const trustHoldingsSuggestions = useMemo(
    () => entitySuggestionsV2?.filter(isEstateHoldingSuggestion) ?? [],
    [entitySuggestionsV2]
  );

  // Modal state for Edit Entity Modal
  const [
    { isModalOpen: isEditEntityModalOpen, data: editEntityModalData },
    { openModal: openEditEntityModal, closeModal: closeEditEntityModal },
  ] = useModalState<{
    entityType: EntityType;
    initalSection: EditEntitySection;
  }>();

  // Modal state for AI Onboarding
  const [aiOnboardingState, aiOnboardingModal] =
    useModalState<
      Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>
    >();

  // Modal state for Suggestion Document Drawer
  const [suggestionDrawerState, suggestionDrawerModal] = useModalState<{
    suggestionId: string;
  }>();

  const handleOpenAIOnboardingModal = useCallback(
    ({
      initialSelectedSuggestion,
    }: Pick<OpenAIOnboardingModalProps, 'initialSelectedSuggestion'>) => {
      diagnostics.debug('[TrustSection] Opening AI Onboarding Modal', {
        initialSelectedSuggestion,
      });
      aiOnboardingModal.openModal({ initialSelectedSuggestion });
    },
    [aiOnboardingModal]
  );

  const handleNavigateToEntitySection = useCallback(
    (section: EditEntitySection) => {
      if (onEditEntitySection) {
        onEditEntitySection(trust.id, section);
        return;
      }

      const entityType = getEntityTypeFromEntityKind(trust.kind);
      openEditEntityModal({ entityType, initalSection: section });
    },
    [onEditEntitySection, openEditEntityModal, trust.id, trust.kind]
  );

  const handleAddDispositions = useCallback(() => {
    handleNavigateToEntitySection(
      EditEntitySection.DISPOSITIVE_PROVISIONS_GRANTOR_1
    );
  }, [handleNavigateToEntitySection]);

  const handleLocateSuggestion = useCallback(() => {
    const suggestion = trust.onboardingEntitySuggestion?.[0];
    if (suggestion) {
      suggestionDrawerModal.openModal({ suggestionId: suggestion.id });
    }
  }, [trust.onboardingEntitySuggestion, suggestionDrawerModal]);

  const subtitle = useFormattedTrustSubtitle(trust);

  const handleTitleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!householdId) return;

    // draft GRATs go to the structuring designer or implementation depending on their stage
    if (variant === 'draft_grats') {
      return navigateToGRATPageByStage(trust.stage, trust.id);
    }

    const entityDetailsUrl = getCompletePathFromRouteKey(
      ROUTE_KEYS.HOUSEHOLD_ENTITY_DETAILS,
      {
        entityId: trust.id,
        householdId,
      }
    );
    navigate(entityDetailsUrl);
  };

  return (
    <>
      <Accordion
        data-testid={`estate-trust-accordion-${getIdSafeString(trust.subtype.displayName)}`}
        variant="filled"
        responsiveRightAdornment
        title={
          <Stack direction="row" gap={1} alignItems="center">
            <Typography
              variant="title-xs"
              onClick={handleTitleClick}
              sx={{
                textDecoration: isHovered ? 'underline' : 'none',
                cursor: isHovered ? 'pointer' : 'default',
                transition: 'text-decoration 0.2s ease, cursor 0.2s ease',
              }}
            >
              {trust.subtype.displayName}
            </Typography>
            {variant === 'default' && (
              <NoDispositionsBadge
                entityName={trust.subtype.displayName}
                entityKind={trust.kind}
                dispositionScenarios={trust.subtype.dispositionScenarios}
                onAddDispositions={handleAddDispositions}
                entityId={trust.id}
                entitySubtypeId={trust.subtype.id}
                entityType={getEntityTypeFromEntityKind(trust.kind)}
              />
            )}
            {isHovered &&
              trust.onboardingEntitySuggestion?.[0] &&
              variant === 'default' && (
                <LocateSuggestionButton
                  suggestionID={trust.onboardingEntitySuggestion[0].id}
                  onLocateSuggestionClick={handleLocateSuggestion}
                  dataTestId={`trust-locate-suggestion-${trust.id}`}
                  iconProps={{ size: 12 }}
                  sx={{
                    width: '16px',
                    minWidth: '16px',
                    height: '16px',
                    p: '2px',
                  }}
                />
              )}
          </Stack>
        }
        subtitle={
          <TypographyClamped
            variant="body-xs"
            lines={1}
            tooltipProps={{ title: subtitle }}
          >
            {subtitle}
          </TypographyClamped>
        }
        isDefaultExpanded={false}
        expansionDisabled={variant === 'draft_grats'}
        {...hoverProps}
        rightAdornment={
          <Stack
            direction="row"
            spacing={1}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            {variant === 'default' && (
              <HoldingsGroupActions
                entity={trust}
                onAddHoldingClick={() => setIsAddHoldingModalOpen(true)}
                onLinkIntegrationClick={handleLinkIntegrationClick}
                isHovered={isHovered}
                isGeneratingSuggestions={isGeneratingSuggestions}
                holdingsSuggestions={trustHoldingsSuggestions}
                entitySuggestionsV2={unacknowledgedSuggestionsV2[trust.id]}
                onHoldingsSuggestionClick={() => {
                  handleOpenAIOnboardingModal({
                    initialSelectedSuggestion: undefined,
                  });
                }}
                onEditEntitySectionSuggestionClick={
                  handleNavigateToEntitySection
                }
              />
            )}
            {variant === 'draft_grats' && <DraftGRATActions entity={trust} />}
            {variant === 'default' &&
              (canEdit || canManageIntegrationData) &&
              householdId && (
                <EntityActionsMenu
                  entity={trust}
                  householdId={householdId}
                  contextMenuButtonProps={{
                    size: 'xs',
                    variant: 'transparent',
                  }}
                  onAfterDelete={refetchEstatePageData}
                  onAfterConvert={refetchEstatePageData}
                  onAfterDuplicate={refetchEstatePageData}
                  onEdit={handleNavigateToEntitySection}
                />
              )}
          </Stack>
        }
        withDivider={false}
      >
        {variant === 'default' && (
          <Stack spacing={1}>
            {canEdit && (
              <AISuggestionReviewCard
                variant={AIOnboardingModalVariant.HOLDINGS}
                suggestionDisplayText={{
                  singular: 'holding',
                  plural: 'holdings',
                }}
                suggestions={trustHoldingsSuggestions}
                additionalReferenceSuggestions={additionalReferenceSuggestions}
                onOpenAIOnboardingModal={handleOpenAIOnboardingModal}
                showProcessingBanner={false}
                onSuggestionCreated={refetchSuggestions}
              />
            )}
            <EntityAccordionContent entity={trust} />
          </Stack>
        )}
      </Accordion>
      <AddHoldingsFormModal
        holdingsGroup={trust}
        open={isAddHoldingModalOpen}
        onClose={() => setIsAddHoldingModalOpen(false)}
      />
      <LinkIntegrationEntityFormModal
        entityId={trust.id}
        open={isLinkIntegrationModalOpen}
        onClose={() => setIsLinkIntegrationModalOpen(false)}
      />
      <IntegrationConnectionWarningModal
        isOpen={isIntegrationConnectionWarningModalOpen}
        onClose={() => setIsIntegrationConnectionWarningModalOpen(false)}
        canConnectToIntegrations={canConnectToIntegrations}
      />
      {aiOnboardingState.isModalOpen && (
        <AIOnboardingModal
          isOpen={aiOnboardingState.isModalOpen}
          onClose={() => aiOnboardingModal.closeModal()}
          householdId={householdId!}
          variant={AIOnboardingModalVariant.HOLDINGS}
          initialSelectedSuggestion={
            aiOnboardingState.data?.initialSelectedSuggestion
          }
          suggestionsWhere={getUnacknowledgedHoldingsSuggestionsWhere(trust.id)}
          additionalReferenceSuggestions={additionalReferenceSuggestions}
          skipNavigationOnComplete={true}
        />
      )}
      {suggestionDrawerState.isModalOpen && suggestionDrawerState.data && (
        <AIOnboardingSuggestionDrawer
          isOpen={suggestionDrawerState.isModalOpen}
          onClose={() => suggestionDrawerModal.closeModal()}
          suggestionId={suggestionDrawerState.data.suggestionId}
        />
      )}
      {isEditEntityModalOpen && editEntityModalData && (
        <EntityDetailsAwareRoute entityId={trust.id}>
          <EntitySuggestionsProvider>
            <DispositiveProvisionSuggestionsProvider targetId={trust.id}>
              <EditEntitySplitScreenModal
                isOpen={isEditEntityModalOpen}
                entityType={editEntityModalData.entityType}
                initialSection={editEntityModalData.initalSection}
                onClose={closeEditEntityModal}
              />
            </DispositiveProvisionSuggestionsProvider>
          </EntitySuggestionsProvider>
        </EntityDetailsAwareRoute>
      )}
    </>
  );
}
